import React, { useState, useEffect } from 'react';
import { X, Briefcase, PieChart, Edit2, Trash2, Plus, Sparkles, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card } from './ui/card';
import { mockBrokers } from '../mock';
import DescriptionEditorModal from './DescriptionEditorModal';

const EditPortfolioModal = ({ isOpen, onClose, portfolio, onSave }) => {
  const [portfolioName, setPortfolioName] = useState(portfolio?.name || '');
  const [portfolioSize, setPortfolioSize] = useState(portfolio?.totalSize || 0);
  const [categories, setCategories] = useState(portfolio?.categories || []);
  const [selectedBrokers, setSelectedBrokers] = useState(portfolio?.brokers || []);
  const [isSaving, setIsSaving] = useState(false);
  const [editingDescriptionIndex, setEditingDescriptionIndex] = useState<number | null>(null);

  // Update state when portfolio changes
  useEffect(() => {
    if (portfolio) {
      setPortfolioName(portfolio.name || '');
      setPortfolioSize(portfolio.totalSize || 0);
      setCategories(portfolio.categories || []);
      setSelectedBrokers(portfolio.brokers || []);
    }
  }, [portfolio]);

  const handleAddCategory = () => {
    setCategories([
      ...categories,
      { name: 'New Category', percentage: 0, timeframe: 'Monthly', color: '#4169E1', description: '' }
    ]);
  };

  const handleUpdateCategory = (index, field, value) => {
    const newCategories = [...categories];
    if (field === 'percentage') {
      newCategories[index][field] = Number(value);
    } else {
      newCategories[index][field] = value;
    }
    setCategories(newCategories);
  };

  const handleDeleteCategory = (index) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const toggleBroker = (brokerId) => {
    if (selectedBrokers.includes(brokerId)) {
      setSelectedBrokers(selectedBrokers.filter(id => id !== brokerId));
    } else {
      setSelectedBrokers([...selectedBrokers, brokerId]);
    }
  };

  const totalAllocation = categories.reduce((sum, cat) => sum + parseInt(cat.percentage || 0), 0);
  const allCategoriesHaveDescription = categories.every(cat => cat.description && cat.description.trim().length > 0);
  const isValid = totalAllocation === 100 && allCategoriesHaveDescription;

  const handleSave = async () => {
    if (!isValid || !portfolio?.id) {
      return;
    }

    setIsSaving(true);
    
    try {
      await onSave(portfolio.id, {
        name: portfolioName,
        totalSize: parseInt(portfolioSize),
        categories: categories.map(cat => ({
          ...cat,
          percentage: typeof cat.percentage === 'string' ? parseInt(cat.percentage) : cat.percentage
        })),
        brokers: selectedBrokers
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl">Edit Portfolio - {portfolio?.name}</DialogTitle>
              <p className="text-sm text-gray-500 mt-1">Manage your portfolio settings and allocations</p>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
          {/* Left Column - Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Portfolio Information */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold">Portfolio Information</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="portfolio-name">Portfolio Name</Label>
                  <Input
                    id="portfolio-name"
                    value={portfolioName}
                    onChange={(e) => setPortfolioName(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="portfolio-size">Total Portfolio Size (₹)</Label>
                  <Input
                    id="portfolio-size"
                    type="number"
                    value={portfolioSize}
                    onChange={(e) => setPortfolioSize(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="mb-2 block">Linked Brokers</Label>
                  <div className="flex flex-wrap gap-2">
                    {mockBrokers.map((broker) => (
                      <button
                        key={broker.id}
                        onClick={() => toggleBroker(broker.id)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          selectedBrokers.includes(broker.id)
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-semibold mr-2">{broker.logo}</span>
                        {broker.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Category Allocation */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold">Category Allocation</h3>
                </div>
                <span className={`text-sm font-medium ${
                  isValid ? 'text-green-600' : 'text-red-600'
                }`}>
                  Total: {totalAllocation}%
                </span>
              </div>

              <div className="space-y-4">
                {categories.map((category, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Input
                        type="color"
                        value={category.color}
                        onChange={(e) => handleUpdateCategory(index, 'color', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={category.name}
                        onChange={(e) => handleUpdateCategory(index, 'name', e.target.value)}
                        className="flex-1"
                        placeholder="Category name"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCategory(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <Label className="text-xs">Allocation %</Label>
                        <Input
                          type="number"
                          value={category.percentage}
                          onChange={(e) => handleUpdateCategory(index, 'percentage', e.target.value)}
                          className="mt-1"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Timeframe</Label>
                        <Select
                          value={category.timeframe}
                          onValueChange={(value) => handleUpdateCategory(index, 'timeframe', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Daily">Daily</SelectItem>
                            <SelectItem value="Weekly">Weekly</SelectItem>
                            <SelectItem value="Bi-weekly">Bi-weekly</SelectItem>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                            <SelectItem value="Quarterly">Quarterly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Category Description - Compact with Expand Button */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-3 h-3 text-purple-500" />
                          <Label className="text-xs font-medium">Category Description</Label>
                          <span className="text-red-500 text-xs">*</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1 text-purple-600 border-purple-300 hover:bg-purple-50"
                          onClick={() => setEditingDescriptionIndex(index)}
                        >
                          <Expand className="w-3 h-3" />
                          {category.description ? 'Edit' : 'Add Description'}
                        </Button>
                      </div>
                      
                      {category.description ? (
                        <div className="text-sm text-gray-600 line-clamp-2 bg-white p-2 rounded border">
                          {category.description}
                        </div>
                      ) : (
                        <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                          ⚠️ Description required for AI portfolio review
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-400 text-right mt-1">
                        {(category.description || '').length}/10,000 chars
                      </p>
                    </div>
                  </div>
                ))}

                <Button
                  variant="outline"
                  className="w-full border-dashed border-2"
                  onClick={handleAddCategory}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </div>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Allocation Overview</h3>
              
              {/* Pie Chart */}
              <div className="flex items-center justify-center mb-6">
                <svg width="200" height="200" viewBox="0 0 200 200">
                  {categories.map((category, index) => {
                    const percentage = parseInt(category.percentage || 0);
                    
                    // Special case: if 100% allocation, draw a full circle
                    if (percentage >= 100) {
                      return (
                        <circle
                          key={category.name}
                          cx="100"
                          cy="100"
                          r="90"
                          fill={category.color}
                          opacity="0.9"
                        />
                      );
                    }

                    const totalPercentage = categories.slice(0, index).reduce((acc, cat) => acc + parseInt(cat.percentage || 0), 0);
                    const startAngle = (totalPercentage / 100) * 360;
                    const endAngle = startAngle + (percentage / 100) * 360;
                    const largeArcFlag = percentage > 50 ? 1 : 0;

                    const startX = 100 + 90 * Math.cos((Math.PI * startAngle) / 180);
                    const startY = 100 + 90 * Math.sin((Math.PI * startAngle) / 180);
                    const endX = 100 + 90 * Math.cos((Math.PI * endAngle) / 180);
                    const endY = 100 + 90 * Math.sin((Math.PI * endAngle) / 180);

                    return (
                      <path
                        key={category.name}
                        d={`M 100 100 L ${startX} ${startY} A 90 90 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                        fill={category.color}
                        opacity="0.9"
                      />
                    );
                  })}
                </svg>
              </div>

              {/* Category List */}
              <div className="space-y-3">
                {categories.map((category) => (
                  <div key={category.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{category.percentage}%</p>
                      <p className="text-xs text-gray-500">₹{((portfolioSize * category.percentage) / 100).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Warning */}
            {portfolioSize < portfolio?.totalSize && (
              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-600 text-lg">⚠️</span>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Portfolio Size Reduction</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Reducing total size below ₹{(portfolio?.totalSize || 0).toLocaleString('en-IN')} may require selling current positions.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {!allCategoriesHaveDescription && totalAllocation === 100 && (
              <Card className="p-4 bg-purple-50 border-purple-200">
                <div className="flex items-start gap-2">
                  <span className="text-purple-600 text-lg">✍️</span>
                  <div>
                    <p className="text-sm font-medium text-purple-800">Category Descriptions Required</p>
                    <p className="text-xs text-purple-700 mt-1">
                      Each category needs a description for AI portfolio review.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 mt-6 pt-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={!isValid || isSaving}
            onClick={handleSave}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>

      {/* Description Editor Modal */}
      {editingDescriptionIndex !== null && (
        <DescriptionEditorModal
          isOpen={editingDescriptionIndex !== null}
          onClose={() => setEditingDescriptionIndex(null)}
          onSave={(description) => {
            handleUpdateCategory(editingDescriptionIndex, 'description', description);
            setEditingDescriptionIndex(null);
          }}
          categoryName={categories[editingDescriptionIndex]?.name || ''}
          categoryColor={categories[editingDescriptionIndex]?.color || '#4169E1'}
          initialDescription={categories[editingDescriptionIndex]?.description || ''}
        />
      )}
    </Dialog>
  );
};

export default EditPortfolioModal;
